import { useEffect } from 'react';
import { toast } from 'sonner';
import config from '@config';
import { isReachable } from '@shared/lib/hooks';
import { Logger } from '@shared/lib/logger';
import { translate } from '@shared/i18n';

const logger = new Logger('sw-version');

// Guards against a mismatch that never actually resolves -- e.g. an edge/CDN
// serving this bundle and `/build` from two different deployments
// inconsistently, so every single load re-detects "newer version available"
// and reloads again, forever. `sessionStorage` (not `localStorage`): a fresh
// tab/session deserves a fresh attempt count rather than inheriting a stale
// exhausted one from hours or days ago, and it still survives the reloads
// *within* one bad session, which is the case this is actually guarding.
const RELOAD_ATTEMPTS_KEY = 'cf-build-version-reload-attempts';
export const MAX_RELOAD_ATTEMPTS = 3;

// The mismatch this guards against is usually a *propagation* window, not a
// permanent disagreement: a CDN edge that served this bundle from one
// deployment and `/build` from another catches up once the new deploy has
// finished rolling out across its edge nodes, typically within seconds --
// it doesn't self-correct instantly. Reloading three times back to back
// gives that window essentially no time to close before the cap above gives
// up, which is exactly the symptom this was reported against: a few fast
// white-flash reloads, then stuck on a stale build until the reader clears
// the cache by hand. Backing off (2s, 4s, 8s) trades a few extra seconds of
// "still on the old page" for a real chance the *next* attempt lands on a
// build that actually agrees with itself.
const RELOAD_BACKOFF_BASE_MS = 2000;

function getReloadAttempts(): number {
	try {
		return Number(sessionStorage.getItem(RELOAD_ATTEMPTS_KEY)) || 0;
	} catch {
		// Storage unavailable (private browsing, quota, disabled) -- can't
		// count, so can't cap either; fall through and let the reload
		// happen as if this were the first attempt every time. Worse than
		// having the cap, but no worse than before this existed.
		return 0;
	}
}

function resetReloadAttempts(): void {
	try {
		sessionStorage.removeItem(RELOAD_ATTEMPTS_KEY);
	} catch {
		// Same as above -- nothing to clean up if storage never worked.
	}
}

/**
 * The one place every `applyUpdate()` reload path funnels through, so the
 * cap (and the backoff below) apply uniformly regardless of which of the
 * four reload call sites fires. Past `MAX_RELOAD_ATTEMPTS`, logs and gives
 * up instead of reloading again -- a reader stuck on a stale build is a
 * better outcome than a page that never stops refreshing.
 */
function reloadPage(): void {
	const attempts = getReloadAttempts() + 1;
	if (attempts > MAX_RELOAD_ATTEMPTS) {
		logger.error(
			`Giving up after ${MAX_RELOAD_ATTEMPTS} reload attempts -- the build mismatch never resolved. Not reloading again.`,
		);
		return;
	}
	try {
		sessionStorage.setItem(RELOAD_ATTEMPTS_KEY, String(attempts));
	} catch {
		// See getReloadAttempts -- can't persist the count, but the reload
		// itself is still safe to attempt.
	}
	// See `RELOAD_BACKOFF_BASE_MS`'s own comment for why this waits instead
	// of reloading immediately. `2 ** (attempts - 1)`: 2s, then 4s, then 8s.
	const delay = RELOAD_BACKOFF_BASE_MS * 2 ** (attempts - 1);
	setTimeout(() => {
		window.location.reload();
	}, delay);
}

export type BuildCheckResult = 'updating' | 'current' | 'offline' | 'failed';

let checkInFlight: Promise<BuildCheckResult> | null = null;

/**
 * One check of this bundle's build against the deployed `/build` file;
 * `useBuildVersionCheck` below runs it on boot, on reconnect and on return
 * to the foreground, and Settings → About runs it on demand ("Check for
 * updates"). Concurrent calls share one request. `updating` means a newer
 * build is live and the reload that applies it has started.
 */
export function checkForNewBuild(): Promise<BuildCheckResult> {
	checkInFlight ??= runBuildCheck().finally(() => {
		checkInFlight = null;
	});
	return checkInFlight;
}

async function runBuildCheck(): Promise<BuildCheckResult> {
	if (!navigator.onLine) {
		return 'offline';
	}
	try {
		// Same bug as use-online.ts had: `isReachable` resolves a
		// boolean rather than rejecting, so the two-callback `.then`
		// mapped both outcomes to `true` and this check never
		// short-circuited.
		const online = await isReachable(config.build.host);
		if (!online) {
			return 'offline';
		}

		const url = `${import.meta.env.BASE_URL}build?t=${Date.now()}`;
		const response = await fetch(url, { cache: 'no-store' });
		if (!response.ok) {
			return 'failed';
		}
		const remoteBuildNumber = (await response.text()).trim();
		const currentBuildNumber = String(config.build.buildNumber);
		if (remoteBuildNumber && remoteBuildNumber !== currentBuildNumber) {
			logger.info(
				`New build detected (${currentBuildNumber} -> ${remoteBuildNumber}), applying update`,
			);
			// Reload itself is what actually applies the update
			// (`applyUpdate()` below, then `reloadPage()`'s own 2s+
			// backoff) -- the toast fires first so there's a moment for
			// it to actually be seen before the page goes away.
			toast.info(translate('common.updateAvailable'));
			await applyUpdate();
			return 'updating';
		}
		if (remoteBuildNumber) {
			// This build matches what `/build` reports -- whatever
			// reload attempts got us here worked. Reset the counter
			// so a *future*, genuinely new mismatch gets the full
			// cap again, instead of inheriting a count left over
			// from this now-resolved one.
			resetReloadAttempts();
			return 'current';
		}
		return 'failed';
	} catch {
		// Offline or the request failed — this check is purely additive.
		return 'failed';
	}
}

/**
 * On boot and whenever the app regains connectivity/foreground, compares the
 * build identifier baked into this bundle (config.build.buildNumber) against
 * the `/build` file the pipeline writes at deploy time. A mismatch means a
 * newer version is live: trigger the service worker's own update check
 * (its precache manifest — and therefore its byte content — differs on every
 * build, so the browser's SW update diff reliably fires) and reload once the
 * new worker takes control. Never runs while offline, and a failed/timed-out
 * fetch is treated the same as "nothing to do" — an offline session is never
 * disrupted by this check.
 *
 * `navigator.onLine` only reflects whether the device has a network
 * interface up — a captive portal, a VPN that's connected but not routing,
 * or plain DNS trouble all report `true` there while nothing actually
 * resolves. Bail out fast on `navigator.onLine === false` (a cheap, reliable
 * true-negative), but treat it as necessary and not sufficient: before doing
 * anything else, confirm the GraphQL host (`config.build.host`, the same
 * app-critical dependency `useIsOnline` checks) is actually reachable. Only
 * then is a `/build` mismatch trusted enough to bust the shell cache and
 * reload — a false "online" reading must never trigger that.
 */
export function useBuildVersionCheck() {
	useEffect(() => {
		const checkVersion = () => {
			void checkForNewBuild();
		};

		checkVersion();

		const onVisibilityChange = () => {
			if (document.visibilityState === 'visible') {
				checkVersion();
			}
		};

		window.addEventListener('online', checkVersion);
		document.addEventListener('visibilitychange', onVisibilityChange);

		return () => {
			window.removeEventListener('online', checkVersion);
			document.removeEventListener(
				'visibilitychange',
				onVisibilityChange,
			);
		};
	}, []);
}

async function applyUpdate() {
	if (!('serviceWorker' in navigator)) {
		reloadPage();
		return;
	}

	try {
		const registration = await navigator.serviceWorker.getRegistration();
		if (!registration) {
			reloadPage();
			return;
		}

		const reloadOnce = (() => {
			let reloaded = false;
			return () => {
				if (reloaded) {
					return;
				}
				reloaded = true;
				reloadPage();
			};
		})();

		navigator.serviceWorker.addEventListener(
			'controllerchange',
			reloadOnce,
			{
				once: true,
			},
		);

		// Belt-and-suspenders fallback for when controllerchange never fires
		// (e.g. the update check finds nothing new to install). Only arm it
		// once we know a new worker actually reached the "installed" state —
		// starting a fixed timer immediately would reload the page while a
		// new worker is still precaching the app shell on a slow connection,
		// before it can ever take control, producing a reload loop.
		const armFallbackReload = (worker: ServiceWorker) => {
			const maybeSchedule = () => {
				if (
					worker.state === 'installed' ||
					worker.state === 'activated'
				) {
					setTimeout(reloadOnce, 3000);
				}
				// 'redundant' means install failed — nothing to reload for.
			};
			worker.addEventListener('statechange', maybeSchedule);
			maybeSchedule();
		};

		if (registration.installing) {
			armFallbackReload(registration.installing);
		} else {
			registration.addEventListener(
				'updatefound',
				() => {
					if (registration.installing) {
						armFallbackReload(registration.installing);
					}
				},
				{ once: true },
			);
		}

		await registration.update();
	} catch {
		reloadPage();
	}
}
